import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteSession, cancelSession, patchSessionStatus } from "../../services/adminTimeTableServices";
import { generateSessionZoomLink } from "../../services/sessionBookingService";
import SessionModal from "./SessionModal";
import ChangeSessionTimeModal from "./ChangeSessionTimeModal";
import { utcToLocalTime } from "../../../utils/dateTimeUtils";

const STATUS_NEXT  = { DRAFT: "ACTIVE", ACTIVE: "INACTIVE", INACTIVE: "DRAFT" };
const STATUS_BADGE = { DRAFT: "tt-badge-draft", ACTIVE: "tt-badge-active", INACTIVE: "tt-badge-inactive" };
const STATUS_NEXT_LABEL = { DRAFT: "Activate", ACTIVE: "Deactivate", INACTIVE: "→ Draft" };

const LEVEL_COLORS = { BEGINNER: "#dcfce7", INTERMEDIATE: "#fef9c3", ADVANCED: "#fee2e2" };
const LEVEL_TEXT   = { BEGINNER: "#15803d", INTERMEDIATE: "#b45309", ADVANCED: "#dc2626" };

const SessionTable = ({ dayId, dayDate, sessions, reload, onSessionUpdated }) => {
  const [modalOpen, setModalOpen]               = useState(false);
  const [selectedSession, setSelectedSession]   = useState(null);
  const [timeModalOpen, setTimeModalOpen]       = useState(false);
  const [selectedTimeSession, setSelectedTimeSession] = useState(null);
  const [loadingZoom, setLoadingZoom]           = useState({});
  const [loadingCancel, setLoadingCancel]       = useState({});
  const [loadingStatus, setLoadingStatus]       = useState({});
  const [localZoomLinks, setLocalZoomLinks]     = useState({});
  const navigate = useNavigate();

  const isExpired  = (endTime) => endTime && new Date(endTime) < new Date();
  const getZoomLink = (s) => localZoomLinks[s.id] || s.zoomLink || s.zoom_link;

  const handleStatusToggle = async (s) => {
    const next = STATUS_NEXT[s.status] || "ACTIVE";
    setLoadingStatus((p) => ({ ...p, [s.id]: true }));
    try { await patchSessionStatus(s.id, next); reload(); }
    catch { alert("Failed to update status"); }
    finally { setLoadingStatus((p) => ({ ...p, [s.id]: false })); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this session?")) return;
    try { await deleteSession(id); reload(); }
    catch { alert("Failed to delete session"); }
  };

  const handleCancel = async (sessionId) => {
    if (!window.confirm("Cancel this session? This cannot be undone.")) return;
    setLoadingCancel((p) => ({ ...p, [sessionId]: true }));
    try { await cancelSession(sessionId); reload(); }
    catch { alert("Failed to cancel session"); }
    finally { setLoadingCancel((p) => ({ ...p, [sessionId]: false })); }
  };

  const handleGenerateZoom = async (sessionId) => {
    setLoadingZoom((p) => ({ ...p, [sessionId]: true }));
    try {
      const result   = await generateSessionZoomLink(sessionId);
      const zoomLink = result?.data?.zoomLink || result?.zoomLink;
      if (zoomLink) { setLocalZoomLinks((p) => ({ ...p, [sessionId]: zoomLink })); reload(); }
      else alert(result?.data?.message || result?.message || "Zoom link not available");
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to generate Zoom link");
    } finally {
      setLoadingZoom((p) => ({ ...p, [sessionId]: false }));
    }
  };

  return (
    <div>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <span style={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 600 }}>
          {sessions.length} session{sessions.length !== 1 ? "s" : ""}
        </span>
        <button className="adm-btn adm-btn-success adm-btn-sm"
          onClick={() => { setSelectedSession(null); setModalOpen(true); }}>
          + Add Session
        </button>
      </div>

      {sessions.length === 0 ? (
        <div style={{ textAlign: "center", padding: "28px 0", color: "#94a3b8", fontSize: "0.85rem" }}>
          No sessions yet. Click "+ Add Session" to create one.
        </div>
      ) : (
        <div className="tt-session-table-wrap">
          <table className="tt-session-table">
            <thead>
              <tr>
                <th>Topic</th>
                <th>Time</th>
                <th>Level</th>
                <th>Plan</th>
                <th>Speaker</th>
                <th>Content</th>
                <th>Slots</th>
                <th>Visibility</th>
                <th>Zoom</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => {
                const expired   = isExpired(s.endTime);
                const cancelled = s.isCancelled === true;
                const zoomLink  = getZoomLink(s);

                return (
                  <tr key={s.id} style={{ opacity: cancelled ? 0.6 : 1 }}>
                    {/* Topic */}
                    <td style={{ maxWidth: 160 }}>
                      {s.topic
                        ? <span style={{ fontWeight: 600, color: "#0f172a" }}>{s.topic}</span>
                        : <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>No topic</span>}
                    </td>

                    {/* Time */}
                    <td style={{ whiteSpace: "nowrap", fontWeight: 500 }}>
                      {utcToLocalTime(s.startTime)} – {utcToLocalTime(s.endTime)}
                    </td>

                    {/* Level */}
                    <td>
                      {s.level
                        ? <span style={{ fontSize: "0.7rem", fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: LEVEL_COLORS[s.level] || "#f1f5f9", color: LEVEL_TEXT[s.level] || "#475569" }}>{s.level}</span>
                        : <span style={{ color: "#cbd5e1" }}>—</span>}
                    </td>

                    {/* Plan */}
                    <td style={{ fontSize: "0.8rem", color: "#475569" }}>{s.planType || "—"}</td>

                    {/* Speaker */}
                    <td style={{ fontSize: "0.8rem" }}>
                      {s.speakerName
                        ? <span style={{ fontWeight: 500 }}>{s.speakerName}</span>
                        : <span style={{ color: "#cbd5e1" }}>Unassigned</span>}
                    </td>

                    {/* Content */}
                    <td>
                      {s.contentType
                        ? <span className="tt-badge tt-badge-content">{s.contentType}</span>
                        : <span style={{ color: "#cbd5e1" }}>—</span>}
                    </td>

                    {/* Slots */}
                    <td style={{ textAlign: "center", fontWeight: 600, color: "#0f172a" }}>{s.totalSlots}</td>

                    {/* Visibility */}
                    <td>
                      <div className="tt-status-cell">
                        {cancelled
                          ? <span className="tt-badge tt-badge-cancelled">Cancelled</span>
                          : <span className={`tt-badge ${STATUS_BADGE[s.status] || "tt-badge-inactive"}`}>{s.status || "—"}</span>}
                        {!cancelled && (
                          <button className="tt-text-btn" style={{ fontSize: "0.68rem" }}
                            onClick={() => handleStatusToggle(s)}
                            disabled={loadingStatus[s.id]}>
                            {loadingStatus[s.id] ? "…" : STATUS_NEXT_LABEL[s.status]}
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Zoom */}
                    <td>
                      {cancelled ? (
                        <span className="tt-badge tt-badge-cancelled">Cancelled</span>
                      ) : expired ? (
                        <span className="tt-badge tt-badge-expired">Expired</span>
                      ) : zoomLink && zoomLink !== "null" ? (
                        <a href={zoomLink} target="_blank" rel="noreferrer"
                          className="tt-text-btn success" style={{ textDecoration: "none" }}>
                          🔗 Join
                        </a>
                      ) : (
                        <button className="tt-text-btn primary" onClick={() => handleGenerateZoom(s.id)}
                          disabled={loadingZoom[s.id]}>
                          {loadingZoom[s.id] ? "Generating…" : "Generate"}
                        </button>
                      )}
                    </td>

                    {/* Actions */}
                    <td>
                      <div className="tt-action-group">
                        <button className="tt-icon-btn" title="Edit"
                          onClick={() => { setSelectedSession(s); setModalOpen(true); }}>✏️</button>
                        <button className="tt-icon-btn" title="Change Time"
                          onClick={() => { setSelectedTimeSession(s); setTimeModalOpen(true); }}>🕐</button>
                        <button className="tt-icon-btn" title="Agenda"
                          onClick={() => navigate(`/admin/session_agendas/${s.id}`)}>📝</button>
                        <button className="tt-icon-btn" title="Customers"
                          onClick={() => navigate(`/admin/sessions/${s.id}/customers`)}>👥</button>
                        {!cancelled && (
                          <button className="tt-icon-btn danger" title="Cancel session"
                            onClick={() => handleCancel(s.id)} disabled={loadingCancel[s.id]}>
                            {loadingCancel[s.id] ? "…" : "⊘"}
                          </button>
                        )}
                        <button className="tt-icon-btn danger" title="Delete"
                          onClick={() => handleDelete(s.id)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <SessionModal dayId={dayId} dayDate={dayDate} session={selectedSession}
          onClose={() => setModalOpen(false)} reload={reload} onSessionUpdated={onSessionUpdated} />
      )}
      {timeModalOpen && selectedTimeSession && (
        <ChangeSessionTimeModal session={selectedTimeSession} dayDate={dayDate}
          onClose={() => setTimeModalOpen(false)} reload={reload} />
      )}
    </div>
  );
};

export default SessionTable;
